import { useAuth } from 'auth/context/AuthContext';
import {
  useGetAppointmentsByPatientCodexIdLazyQuery,
  useGetAppointmentsByProviderLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import moment from 'moment/moment';
import { upcomingStatuses } from '../app/my-appointments/components/UpcomingAppointments';
import {
  FHIR_APPOINTMENT_STATUS,
  ORDER_BY_DIRECTION_NUMERIC,
  SORT_FHIR_APPOINTMENT_BY,
} from 'utilities/interfaces';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppointmentTypeEnum } from 'graphql/generated/hasura';
import { AppointmentHasuraStatus } from '../app/my-appointments/const/appointment';
import { Roles } from '../firebase/interfaces';
import { SIGNAL_CHANNELS, useChannelSignal } from './useChannelSignal';
import {
  MY_PATIENTS,
  MY_PATIENTS_BIO_INFO,
  MY_PATIENTS_BIO_INFO_PATIENT_INFO,
  PROVIDERS_PROVIDER_BIO,
} from 'utilities/routes';
import { useNavigate } from 'react-router-dom';
import {
  MILLISECONDS_IN_SEC,
  MINUTES_IN_HOUR,
  SECONDS_IN_MIN,
} from 'utilities/constants/time';
import { getJoinAppointment } from '../app/my-appointments/components/upcoming-appointments/helper/useAppointmentCardHelper';

export interface UpcomingAppointments {
  date: string;
  end?: string;
  appointmentType: AppointmentTypeEnum;
  name: string;
  appointmentCodexId: string;
  patientCodexId: string;
  providerCodexId: string;
  appointmentId?: string;
  status: FHIR_APPOINTMENT_STATUS;
  appointmentStatus?: AppointmentHasuraStatus;
}

export const useGetUpcomingAppointment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointment] = useState<UpcomingAppointments[]>();
  const [currentTime, setCurrentTime] = useState<string>(() =>
    moment().add(-1, 'hours').toISOString(),
  );

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);

  const variables = {
    from: currentTime,
    to: moment(currentTime).add(2, 'hours').toISOString(),
    limit: 3,
    offset: 0,
    appointmentStatus: upcomingStatuses,
    sort: SORT_FHIR_APPOINTMENT_BY.SUMMARY_DATE,
    sortDirection: ORDER_BY_DIRECTION_NUMERIC.ASC,
  };

  const [getAppointmentByPatient] = useGetAppointmentsByPatientCodexIdLazyQuery(
    {
      variables,
      context: {
        ignoreGlobalErrors: true,
      },

      onCompleted: (data) => {
        const appoints =
          data?.getFHIRAppointmentByRequestPatientCodexId?.appointment || [];
        setAppointment(
          appoints.map((appoint) => ({
            date: appoint?.start || '',
            end: appoint?.end || '',
            status: appoint?.status as FHIR_APPOINTMENT_STATUS,
            appointmentStatus:
              appoint?.appointmentStatus as AppointmentHasuraStatus,
            patientCodexId: appoint?.codexPatientId || '',
            appointmentType: appoint?.appointmentType as AppointmentTypeEnum,
            providerCodexId: appoint?.codexProviderId || '',
            name: `${appoint?.providerFirstName} ${appoint?.providerLastName}`,
            appointmentCodexId: appoint?.appointmentCodexId,
            appointmentId: appoint?.appointmentId,
          })),
        );
      },
    },
  );
  const [getAppointmentByProvider] = useGetAppointmentsByProviderLazyQuery({
    variables,
    context: {
      ignoreGlobalErrors: true,
    },
    onCompleted: (data) => {
      const appoints =
        data?.getFHIRAppointmentByRequestProviderId?.appointment || [];
      setAppointment(
        appoints.map((appoint) => ({
          date: appoint?.start || '',
          end: appoint?.end || '',
          status: appoint?.status as FHIR_APPOINTMENT_STATUS,
          appointmentStatus:
            appoint?.appointmentStatus as AppointmentHasuraStatus,
          patientCodexId: appoint?.codexPatientId || '',
          appointmentType: appoint?.appointmentType as AppointmentTypeEnum,
          providerCodexId: appoint?.codexProviderId || '',
          name: `${appoint?.patientFirstName} ${appoint?.patientLastName}`,
          appointmentCodexId: appoint?.appointmentCodexId,
          appointmentId: appoint?.appointmentCodexId,
        })),
      );
    },
  });

  const handleJoinAppointment = (appointment: UpcomingAppointments) => {
    const { pathname, state } = getJoinAppointment(appointment);
    navigate(pathname, { state });
  };

  const handleProviderClick = useCallback(
    (appointment: UpcomingAppointments) => {
      return navigate(PROVIDERS_PROVIDER_BIO, {
        state: { providerId: appointment?.providerCodexId },
      });
    },
    [navigate],
  );

  const handlePatientClick = useCallback(
    (appointment: UpcomingAppointments) => {
      return navigate(
        `${MY_PATIENTS}/${appointment?.patientCodexId}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_INFO_PATIENT_INFO}`,
      );
    },
    [navigate],
  );

  const getUpcomingAppointment = useCallback(() => {
    if (isProvider) {
      return getAppointmentByProvider();
    }
    return getAppointmentByPatient();
  }, [isProvider, getAppointmentByProvider, getAppointmentByPatient]);

  useChannelSignal(
    () => {
      getUpcomingAppointment();
      setCurrentTime(moment().add(-1, 'hours').toISOString());
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    user?.uuid,
  );

  return {
    appointments,
    handleJoinAppointment,
    handleNameClick: isProvider ? handlePatientClick : handleProviderClick,
  };
};

interface ITime {
  hours: number;
  minutes: number;
  seconds: number;
}

export const useAppointmentTimer = (
  date?: string,
  end?: string,
  serverTime?: string,
) => {
  const getInitialServerMs = () =>
    (serverTime ? moment.utc(serverTime) : moment.utc()).valueOf();

  const anchorRef = useRef({
    baseTimeMs: getInitialServerMs(),
    basePerfNow: performance.now(),
  });

  const [currentTime, setCurrentTime] = useState(() =>
    moment.utc(anchorRef.current.baseTimeMs),
  );

  useEffect(() => {
    if (serverTime) {
      const baseTimeMs = moment.utc(serverTime).valueOf();
      anchorRef.current = {
        baseTimeMs,
        basePerfNow: performance.now(),
      };
      setCurrentTime(moment.utc(baseTimeMs));
    }
  }, [serverTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsedMs = performance.now() - anchorRef.current.basePerfNow;
      setCurrentTime(moment.utc(anchorRef.current.baseTimeMs + elapsedMs));
    }, MILLISECONDS_IN_SEC);

    return () => clearInterval(timer);
  }, []);

  if (!date || !end) {
    return {
      timer: '',
      isNotStarted: false,
      isFinish: false,
      time: { hours: 0, minutes: 0, seconds: 0 } as ITime,
    };
  }

  const startTime = moment.utc(date);
  const endTime = moment.utc(end);
  const isNotStarted = currentTime.isSameOrBefore(startTime);
  const isFinish = currentTime.isSameOrAfter(endTime);
  const inputTime = isNotStarted ? startTime : endTime;

  const remainingMs = Math.max(0, inputTime.diff(currentTime));
  const totalSeconds = Math.ceil(remainingMs / MILLISECONDS_IN_SEC);

  const hours = Math.floor(totalSeconds / (SECONDS_IN_MIN * MINUTES_IN_HOUR));
  const totalMinutes = Math.floor(totalSeconds / SECONDS_IN_MIN);
  const minutes = totalMinutes % MINUTES_IN_HOUR;
  const seconds = totalSeconds % SECONDS_IN_MIN;

  const timer = `${totalMinutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  return {
    timer,
    isNotStarted,
    isFinish,
    time: {
      hours,
      minutes,
      seconds,
    } as ITime,
  };
};
